import { CMS_MENU, getRouteAccess, isAccessAllowed } from '../constants/cmsAccess';

const cleanLabel = (value = '') => String(value || '')
  .replace(/^[\p{Extended_Pictographic}\p{Emoji_Presentation}]\uFE0F?\s*/u, '')
  .trim();

const leadingIcon = (value = '') => {
  const text = String(value || '').trim();
  const match = text.match(/^([\p{Extended_Pictographic}\p{Emoji_Presentation}]\uFE0F?)/u);
  return match?.[1] || '';
};

const basePath = (value = '') => String(value || '').split('?')[0];

const EXTRA_DASHBOARD_MODULES = [
  { key:'outward-request', group:'Outward Management', label:'Register Outward Request', icon:'📝', path:'/outward/request' },
  { key:'outward-reports', group:'Outward Management', label:'Outward Reports', icon:'📈', path:'/outward/reports' },
  { key:'stock-management', group:'Inventory', label:'Stock Management', icon:'📦', path:'/stock' },
  { key:'stock-reports', group:'Inventory', label:'Stock Reports', icon:'📊', path:'/stock/reports' },
  { key:'supplier-ledger', group:'Inventory', label:'Supplier Ledger', icon:'🏭', path:'/stock/suppliers' },
  { key:'stock-transfer', group:'Inventory', label:'Stock / Ledger Transfer', icon:'🔁', path:'/stock/transfer' },
  { key:'crate-reconciliation', group:'Storage Management', label:'Crate Reconciliation', icon:'♻️', path:'/crate-reconciliation' },
  { key:'crate-ledger', group:'Storage Management', label:'Crate Issue / Return Ledger', icon:'📒', path:'/storage/crates/ledger' },
  { key:'grading-lines', group:'Outward Management', label:'Grading Lines', icon:'⚙️', path:'/outward/grading' },
  { key:'unloading-work-pay', group:'Employees', label:'Unloading Work & Pay', icon:'📦', path:'/employees/unloading-work' },
  { key:'employee-advances-loans', group:'Employees', label:'Salary Advance & Loans', icon:'💸', path:'/employees/advances-loans' },
  { key:'crm', group:'Business Tools', label:'Cold Storage CRM', icon:'🎯', path:'/crm' },
  { key:'integrations', group:'Business Tools', label:'API & CRM Integrations', icon:'🔌', path:'/integrations' },
  { key:'service-providers', group:'Business Tools', label:'Service Providers', icon:'🧰', path:'/service-providers' },
  { key:'internal-documents', group:'Business Tools', label:'Internal Documents', icon:'📁', path:'/internal-documents' },
  { key:'whatsapp-automation', group:'Business Tools', label:'WhatsApp Automation', icon:'💬', path:'/whatsapp-automation' },
  { key:'bank-reconciliation', group:'Finance Management', label:'Cheque & Reconciliation', icon:'🏦', path:'/finance/reconciliation' },
  { key:'form-fields', group:'Settings', label:'Forms & Fields', icon:'🧩', path:'/form-field-settings' },
  { key:'label-designer', group:'Settings', label:'Label Branding & Designer', icon:'🏷️', path:'/label-designer' },
  { key:'finance-dashboard', group:'Finance Management', label:'Finance Dashboard', icon:'💰', path:'/finance/dashboard' },
  { key:'finance-employee-advances', group:'Finance Management', label:'Employee Advance & Loans', icon:'💸', path:'/finance/employee-advances' },
  { key:'trading-reports', group:'Trading', label:'Trading Reports', icon:'📊', path:'/trading/reports' },
];

const flattenCmsMenu = (menu = []) => {
  const rows = [];
  (Array.isArray(menu) ? menu : []).forEach((item, parentIndex) => {
    const parentLabel = cleanLabel(item?.label || 'General');
    const parentIcon = item?.icon || leadingIcon(item?.label) || '◆';
    if (item?.path && basePath(item.path) !== '/company-dashboard') {
      rows.push({
        key: item.key || `route-${parentIndex}-${basePath(item.path).replace(/\W+/g, '-')}`,
        group: parentLabel,
        label: cleanLabel(item.label),
        icon: item.icon || leadingIcon(item.label) || parentIcon,
        path: item.path,
        accessRule: item,
      });
    }
    (Array.isArray(item?.subItems) ? item.subItems : []).forEach((sub, childIndex) => {
      if (!sub?.path || basePath(sub.path) === '/company-dashboard') return;
      rows.push({
        key: sub.key || `${item.key || parentIndex}-${childIndex}-${basePath(sub.path).replace(/\W+/g, '-')}`,
        group: parentLabel,
        label: cleanLabel(sub.label),
        icon: sub.icon || leadingIcon(sub.label) || parentIcon,
        path: sub.path,
        accessRule: sub,
      });
    });
  });
  return rows;
};

const dedupeModules = (rows = []) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = String(row?.path || '').trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const DASHBOARD_MODULES = Object.freeze(
  dedupeModules([...flattenCmsMenu(CMS_MENU), ...EXTRA_DASHBOARD_MODULES]).map((row, index) => ({
    ...row,
    order: index,
    description: `Open ${row.label} in ${row.group}.`,
  }))
);

export const DEFAULT_DASHBOARD_SETTINGS = Object.freeze({
  hiddenPaths: [],
  order: [],
  showKpis: true,
  showRecentActivity: true,
  showOperationsOverview: true,
  showSystemStatus: true,
  compactCards: false,
  overrides: {},
});

export const normalizeDashboardSettings = (value = {}) => {
  const hiddenPaths = Array.isArray(value?.hiddenPaths)
    ? Array.from(new Set(value.hiddenPaths.map(String).filter(Boolean)))
    : [];
  const order = Array.isArray(value?.order)
    ? Array.from(new Set(value.order.map(String).filter(Boolean)))
    : [];
  const overrides = value?.overrides && typeof value.overrides === 'object' && !Array.isArray(value.overrides)
    ? Object.fromEntries(Object.entries(value.overrides).map(([path, row]) => [String(path), {
        label: String(row?.label || '').slice(0, 80),
        icon: String(row?.icon || '').slice(0, 12),
        description: String(row?.description || '').slice(0, 180),
        cardSize: ['normal','wide'].includes(String(row?.cardSize || '')) ? String(row.cardSize) : 'normal',
      }]))
    : {};
  return {
    ...DEFAULT_DASHBOARD_SETTINGS,
    hiddenPaths,
    order,
    showKpis: value?.showKpis !== false,
    showRecentActivity: value?.showRecentActivity !== false,
    showOperationsOverview: value?.showOperationsOverview !== false,
    showSystemStatus: value?.showSystemStatus !== false,
    compactCards: value?.compactCards === true,
    overrides,
  };
};

export const sortDashboardModules = (modules = [], dashboardSettings = {}) => {
  const settings = normalizeDashboardSettings(dashboardSettings);
  const position = new Map(settings.order.map((path, index) => [path, index]));
  return [...modules].sort((a, b) => {
    const ai = position.has(a.path) ? position.get(a.path) : Number.MAX_SAFE_INTEGER;
    const bi = position.has(b.path) ? position.get(b.path) : Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    return (a.order ?? 9999) - (b.order ?? 9999);
  });
};

export const dashboardModulesForPermissions = (permissions = [], dashboardSettings = {}) => {
  const settings = normalizeDashboardSettings(dashboardSettings);
  const hidden = new Set(settings.hiddenPaths);
  const permitted = DASHBOARD_MODULES.filter((module) => {
    if (hidden.has(module.path)) return false;
    const access = module.accessRule || getRouteAccess(basePath(module.path));
    return isAccessAllowed(permissions, access);
  }).map((module) => {
    const override = settings.overrides?.[module.path] || {};
    return {
      ...module,
      label: override.label || module.label,
      icon: override.icon || module.icon,
      description: override.description || module.description,
      cardSize: override.cardSize || 'normal',
    };
  });
  return sortDashboardModules(permitted, settings);
};

export const toggleDashboardModule = (dashboardSettings = {}, path, visible) => {
  const settings = normalizeDashboardSettings(dashboardSettings);
  const hidden = new Set(settings.hiddenPaths);
  if (visible) hidden.delete(path);
  else hidden.add(path);
  return { ...settings, hiddenPaths: Array.from(hidden) };
};

export const updateDashboardModuleOverride = (dashboardSettings = {}, path, patch = {}) => {
  const settings = normalizeDashboardSettings(dashboardSettings);
  const previous = settings.overrides?.[path] || {};
  return normalizeDashboardSettings({
    ...settings,
    overrides: {
      ...(settings.overrides || {}),
      [path]: { ...previous, ...patch },
    },
  });
};

export const moveDashboardModule = (dashboardSettings = {}, path, direction = -1) => {
  const settings = normalizeDashboardSettings(dashboardSettings);
  const allPaths = sortDashboardModules(DASHBOARD_MODULES, settings).map((item) => item.path);
  const current = allPaths.indexOf(path);
  if (current < 0) return settings;
  const target = Math.max(0, Math.min(allPaths.length - 1, current + Number(direction || 0)));
  if (target === current) return settings;
  const next = [...allPaths];
  const [moved] = next.splice(current, 1);
  next.splice(target, 0, moved);
  return { ...settings, order: next };
};
